import React, {ChangeEvent, useState} from 'react';
import styled from 'styled-components';
import copy from 'copy-to-clipboard';
import {useTranslation} from 'react-i18next';
import {ControlButton, TscButtonType, TextInput, Glyphs, inputTypes} from '@techsmith/tsc-cloud-style-guide';
import config from '../../service/config';
import Constants from '../../constants/Constants';
import {StyledWarning} from '../util/StyledElements';
import '../../static/css/modal.less';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {useTscTheme} from '../../context/themeProvider';
import {mediaStore} from '../../context/mediaProvider';

const DimensionWrapper = styled.div`
   width: 5rem;
`;

const EmbedCodeWrapper = styled.div`
   margin-right: 0.5rem; 
   width: 100%;
`;

const generateEmbedCode = (width: number, height: number, hash: string): string => `<iframe scrolling='no' frameborder='0' style='width: ${width}px; height: ${height}px; border:0;' src='${config.environmentData.PresentationRootUrl}/${hash}/e' allowfullscreen></iframe>`;

let dimensionsWarningTimeoutId: number = null;

export const EmbedCodeModalBase: React.FC<IStateMappedProps> = ({Width, Height, Hash}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [embedWidth, setEmbedWidth] = useState(Width);
   const [embedHeight, setEmbedHeight] = useState(Height);
   const [copyButtonText, setCopyButtonText] = useState<string>(t('general.copy'));
   const [errorText, setErrorText] = useState<string>('');

   React.useEffect(() => () => {
      clearTimeout(dimensionsWarningTimeoutId);
   }, []);

   const onCopyClick = () => {
      copy(generateEmbedCode(embedWidth, embedHeight, Hash), {format: 'text/plain'});
      setCopyButtonText(t('general.copied'));
   };

   const clearErrorAndTimeout = () => {
      setErrorText('');
      clearTimeout(dimensionsWarningTimeoutId);
   };

   const onWidthChange = (e: ChangeEvent<HTMLInputElement>) => {
      clearErrorAndTimeout();
      const width = parseInt(e.target.value.trim(), 10) || 0;
      const height = Math.round(width / (Width / Height));

      setEmbedWidth(width);
      setEmbedHeight(height);

      dimensionsWarningTimeoutId = window.setTimeout(() => {
         if (width !== 0 && width < Constants.embedCode.minWidthWarningInPixel) {
            setErrorText(t('embedCode.smallDimensionsWarning'));
         }
      }, 1000);
   };

   const onHeightChange = (e: ChangeEvent<HTMLInputElement>) => {
      clearErrorAndTimeout();
      const height = parseInt(e.target.value.trim(), 10) || 0;
      const width = Math.round(height * (Width / Height));

      setEmbedWidth(width);
      setEmbedHeight(height);

      dimensionsWarningTimeoutId = window.setTimeout(() => {
         if (width !== 0 && width < Constants.embedCode.minWidthWarningInPixel) {
            setErrorText(t('embedCode.smallDimensionsWarning'));
         }
      }, 1000);
   };

   const onResetClick = () => {
      setEmbedWidth(Width);
      setEmbedHeight(Height);
      clearErrorAndTimeout();
   };

   const onInputBlur = () => {
      if (embedWidth === 0) {
         setEmbedWidth(Width);
      }

      if (embedHeight === 0) {
         setEmbedHeight(Height);
      }
   };

   return (
      <div
         className="themeable-section"
         data-test-id="embed-code-form-field"
      >
         {errorText && (
            <StyledWarning>
               {errorText}
            </StyledWarning>
         )}
         <div className="form-field-group embed-code-dimensions">
            <DimensionWrapper>
               <label htmlFor="embed-code-modal-width">
                  {t('embedCode.width')}
               </label>
               <TextInput
                  sharedId="embed-code-modal-width"
                  testId="embed-code-modal-width"
                  value={embedWidth.toString()}
                  onChange={onWidthChange}
                  themeName={theme}
                  tabIndex={0}
                  onBlur={onInputBlur}
                  type={inputTypes.number}
                  minNumValue={0}
               />
            </DimensionWrapper>
            <DimensionWrapper>
               <label htmlFor="embed-code-modal-height">
                  {t('embedCode.height')}
               </label>
               <TextInput
                  sharedId="embed-code-modal-height"
                  testId="embed-code-modal-height"
                  value={embedHeight.toString()}
                  onChange={onHeightChange}
                  themeName={theme}
                  tabIndex={0}
                  onBlur={onInputBlur}
                  type={inputTypes.number}
                  minNumValue={0}
               />
            </DimensionWrapper>
            <ControlButton
               title={t('embedCode.reset')}
               buttonType={TscButtonType.tertiary}
               themeName={theme}
               onClick={onResetClick}
               testId="reset-button"
               glyph={<Glyphs.Undo16x16 />}
            />
         </div>
         <div className="form-field-group">
            <label>
               {t('embedCode.label')}
               <div className="button-group">
                  <EmbedCodeWrapper>
                     <TextInput
                        sharedId="embed-code-text"
                        testId="embed-code-text"
                        value={generateEmbedCode(embedWidth, embedHeight, Hash)}
                        themeName={theme}
                        tabIndex={0}
                        readOnly={true}
                     />
                  </EmbedCodeWrapper>
                  <ControlButton
                     label={copyButtonText}
                     buttonType={TscButtonType.primary}
                     themeName={theme}
                     onClick={onCopyClick}
                     testId="embed-code-copy-button"
                     width="5rem"
                  />
               </div>
            </label>
         </div>
      </div>
   );
};

export interface IStateMappedProps {
   Width: number;
   Height: number;
   Hash: string;
}

const mapStatesToProps = (
   // eslint-disable-next-line no-empty-pattern
   {},
   {Width, Height, Hash}: Partial<IStateMappedProps>
): IStateMappedProps => ({Width, Height, Hash});

export default withMemoizedContexts(mapStatesToProps, mediaStore)(EmbedCodeModalBase);
